import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import {
  Link2,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  X,
  Search,
} from 'lucide-react';

interface SkuMapping {
  id: string;
  product_id: string;
  local_sku: string;
  shopify_sku: string;
  created_at: string;
}

interface LocalProduct {
  id: string;
  sku: string;
  name: string;
  image_url: string | null;
}

export const SkuMappingPanel: React.FC = () => {
  const { user } = useAuth();
  const [mappings, setMappings] = useState<SkuMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [localSku, setLocalSku] = useState('');
  const [shopifySku, setShopifySku] = useState('');
  const [searchResults, setSearchResults] = useState<LocalProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LocalProduct | null>(null);
  const [showSearch, setShowSearch] = useState(false);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const adminId = getAdminId(user);

  const fetchMappings = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase.rpc('get_sku_mappings', {
        p_admin_id: adminId,
      });
      if (fetchErr) throw fetchErr;
      setMappings(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    fetchMappings();
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [fetchMappings]);

  const searchProducts = useCallback(async (query: string, seq: number) => {
    if (!adminId || query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const sanitized = query.replace(/[%_\\]/g, c => `\\${c}`);
      const { data, error: searchErr } = await supabase
        .from('products')
        .select('id, sku, name, image_url')
        .eq('admin_id', adminId)
        .or(`sku.ilike.%${sanitized}%,name.ilike.%${sanitized}%`)
        .limit(10);

      if (searchSeqRef.current !== seq) return;
      if (searchErr) throw searchErr;
      setSearchResults(data || []);
    } catch (err) {
      if (searchSeqRef.current !== seq) return;
      setSearchResults([]);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      if (searchSeqRef.current === seq) {
        setSearching(false);
      }
    }
  }, [adminId]);

  const handleLocalSkuChange = (value: string) => {
    setLocalSku(value);
    setSelectedProduct(null);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (value.length >= 2) {
      setShowSearch(true);
      setSearching(true);
      const seq = ++searchSeqRef.current;
      searchTimerRef.current = setTimeout(() => {
        searchProducts(value, seq);
      }, 300);
    } else {
      setShowSearch(false);
      setSearchResults([]);
      setSearching(false);
    }
  };

  const selectProduct = (product: LocalProduct) => {
    setSelectedProduct(product);
    setLocalSku(product.sku);
    setShowSearch(false);
    setSearchResults([]);
  };

  const handleAdd = async () => {
    if (!adminId || !selectedProduct || !shopifySku.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const { error: upsertErr } = await supabase.rpc('upsert_sku_mapping', {
        p_admin_id: adminId,
        p_product_id: selectedProduct.id,
        p_local_sku: selectedProduct.sku,
        p_shopify_sku: shopifySku.trim(),
      });
      if (upsertErr) throw upsertErr;

      setSuccess(`Mapped ${selectedProduct.sku} -> ${shopifySku.trim()}`);
      setLocalSku('');
      setShopifySku('');
      setSelectedProduct(null);
      fetchMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mappingId: string) => {
    if (!adminId) return;
    try {
      const { error: delErr } = await supabase.rpc('delete_sku_mapping', {
        p_admin_id: adminId,
        p_mapping_id: mappingId,
      });
      if (delErr) throw delErr;
      setMappings(prev => prev.filter(m => m.id !== mappingId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete mapping');
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
          <Link2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            SKU Mapping
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Map local product SKUs to different Shopify SKUs for image matching
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3 w-3 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-3 w-3 text-green-600" />
          </button>
        </div>
      )}

      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-4">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">Add New Mapping</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,1fr,auto] gap-3 items-end">
          <div className="relative">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Local Product (SKU)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={localSku}
                onChange={e => handleLocalSkuChange(e.target.value)}
                placeholder="Search by SKU or name..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            {selectedProduct && (
              <div className="mt-1 flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                <span className="text-xs text-green-600 dark:text-green-400">
                  {selectedProduct.sku} - {selectedProduct.name}
                </span>
              </div>
            )}
            {showSearch && searchResults.length > 0 && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => selectProduct(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" crossOrigin="anonymous" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-600 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-gray-900 dark:text-gray-100 truncate">{p.sku}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hidden sm:flex items-center justify-center text-gray-400 dark:text-gray-500 pb-1">
            <span className="text-lg font-bold">&rarr;</span>
          </div>

          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Shopify SKU</label>
            <input
              type="text"
              value={shopifySku}
              onChange={e => setShopifySku(e.target.value)}
              placeholder="Enter Shopify SKU..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          <Button
            onClick={handleAdd}
            disabled={!selectedProduct || !shopifySku.trim() || saving}
            loading={saving}
            size="sm"
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
        </div>
      ) : mappings.length === 0 ? (
        <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
          No SKU mappings configured yet. Add a mapping above to link a local SKU to a different Shopify SKU.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Local SKU</th>
                <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400"></th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Shopify SKU</th>
                <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Added</th>
                <th className="py-2 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => (
                <tr key={m.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  <td className="py-2 px-3 font-mono text-gray-900 dark:text-gray-100">{m.local_sku}</td>
                  <td className="py-2 px-3 text-center text-gray-400">&rarr;</td>
                  <td className="py-2 px-3 font-mono text-cyan-600 dark:text-cyan-400">{m.shopify_sku}</td>
                  <td className="py-2 px-3 text-right text-xs text-gray-500 dark:text-gray-400">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
